import { clamp } from 'lodash';
import { colorize, isColor } from '../colors';
/**
 * Interpolation from:
 * https://github.com/react-spring/react-spring/blob/master/src/animated/createInterpolator.ts
 */

/**
 * @param {number} input
 * @param {number[]} inputRange
 *
 * @return {number} The range value
 */

function findRange(input, inputRange) {
  for (var i = 1; i < inputRange.length - 1; ++i) {
    if (inputRange[i] >= input) break;
  }

  return i - 1;
}
/**
 * Base interpolate function.
 *
 * @param {number} [input=0]
 * @param {number} [inputMin=0]
 * @param {number} [inputMax=1]
 * @param {number} [outputMin=0]
 * @param {number} [outputMax=1]
 *
 * @return {number} The interpolated value.
 */


function baseInterpolate(input, inputMin, inputMax, outputMin, outputMax) {
  if (input === void 0) {
    input = 0;
  }

  if (inputMin === void 0) {
    inputMin = 0;
  }

  if (inputMax === void 0) {
    inputMax = 1;
  }

  if (outputMin === void 0) {
    outputMin = 0;
  }

  if (outputMax === void 0) {
    outputMax = 1;
  }

  var result = input;
  if (outputMin === outputMax) return outputMin;
  if (inputMin === inputMax) return input <= inputMin ? outputMin : outputMax; // Input Range

  if (inputMin === -Infinity) result = -result;else if (inputMax === Infinity) result = result - inputMin;else result = (result - inputMin) / (inputMax - inputMin); // Output Range

  if (outputMin === -Infinity) result = -result;else if (outputMax === Infinity) result = result + outputMin;else result = result * (outputMax - outputMin) + outputMin;
  var clampMin = outputMin;
  var clampMax = outputMax;

  if (outputMax < outputMin) {
    clampMin = outputMax;
    clampMax = outputMin;
  }

  return clamp(result, clampMin, clampMax);
}
/**
 * Gets a value based on an input range and an output range.
 * Can be used for a set of numbers or a set of colors.
 *
 * @param {number} [input=0]
 * @param {[number, number]} [inputRange=[0,1]]
 * @param {[number, number]} [outputRange=[0,1]]
 *
 * @return {*} The interpolated value.
 */


export function interpolate(input, inputRange, outputRange) {
  if (input === void 0) {
    input = 0;
  }

  if (inputRange === void 0) {
    inputRange = [0, 1];
  }

  if (outputRange === void 0) {
    outputRange = [0, 1];
  }

  var range = findRange(input, inputRange);
  var outputRange1 = outputRange[range];
  var outputRange2 = outputRange[range + 1];

  if (isColor(outputRange1) && isColor(outputRange2)) {
    var mixAmount = interpolate(input, inputRange, [0, 100]);
    return colorize.mix(outputRange1.toString(), outputRange2.toString(), mixAmount).toRgbString();
  }

  return baseInterpolate(input, inputRange[range], inputRange[range + 1], outputRange1, outputRange2);
}
/**
 * Gets a rounded value based on an input range and an output range.
 *
 * @param {Parameters<interpolate>} args
 *
 * @return {number} The rounded interpolated value.
 */

export function interpolateRounded() {
  return Math.round(interpolate.apply(void 0, arguments));
}